import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogsAPI } from '../services/api';
import { Blog } from '../types';
import { Search, Eye, User, Calendar, TrendingUp, BookOpen } from 'lucide-react';
import { FadeIn, Stagger, StaggerItem } from '../components/animations';
import { Card, CardBody, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const { user } = useAuth();
  const canSeeViews = user?.role === 'admin' || user?.role === 'content-creator';

  useEffect(() => {
    fetchBlogs();
  }, [page, category]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 9 };
      if (category) params.category = category;
      if (search) params.search = search;
      
      const response = await blogsAPI.getAll(params);
      setBlogs(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBlogs();
  };

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'career-tips', label: 'Career Tips' },
    { value: 'tech-news', label: 'Tech News' },
    { value: 'success-stories', label: 'Success Stories' },
    { value: 'fraud-awareness', label: 'Fraud Awareness' },
    { value: 'skills-development', label: 'Skills Development' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Blog & Articles</h1>
            <p className="text-lg text-gray-600">Career tips, success stories, and industry insights</p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <Card className="mb-8">
            <CardBody className="p-6">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search blogs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  <Search className="w-5 h-5" />
                  Search
                </button>
              </form>
            </CardBody>
          </Card>
        </FadeIn>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading blogs...</p>
          </div>
        ) : blogs.length === 0 ? (
          <FadeIn delay={0.2}>
            <Card className="text-center py-16">
              <CardBody>
                <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No blogs found</h3>
                <p className="text-gray-600">Try adjusting your search or filters</p>
              </CardBody>
            </Card>
          </FadeIn>
        ) : (
          <>
            <Stagger>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {blogs.map(blog => (
                  <StaggerItem key={blog._id}>
                    <Link to={`/blogs/${blog.slug}`}>
                      <Card hover className="h-full group cursor-pointer overflow-hidden">
                        {blog.featuredImage && (
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            {blog.isTrending && (
                              <div className="absolute top-4 right-4">
                                <Badge variant="warning">
                                  <TrendingUp className="w-3 h-3 inline mr-1" />
                                  Trending
                                </Badge>
                              </div>
                            )}
                          </div>
                        )}
                        <CardBody className="p-6">
                          <div className="mb-3">
                            <Badge variant="info">
                              {blog.category.replace('-', ' ')}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {blog.title}
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-3">{blog.excerpt}</p>
                          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>{blog.author?.firstName} {blog.author?.lastName}</span>
                            </div>
                            {canSeeViews && (
                              <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                <span>{blog.views}</span>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Link>
                  </StaggerItem>
                ))}
              </div>
            </Stagger>

            {total > 9 && (
              <FadeIn delay={0.3}>
                <div className="flex justify-center items-center gap-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <span className="text-gray-600">
                    Page {page} of {Math.ceil(total / 9)}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(total / 9)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </FadeIn>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blogs;
